// @ts-strict
import { DocumentType, modelOptions, post, pre, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, milliseconds, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import { secondMs } from "../../../timeConstants.js";
import { logBase } from "../../../utils/index.js";
import type {
    ResponsivenessSettingsFieldsWithDefaults,
    ResponsivenessSettingsSchemaTypes,
} from "../../types/responsivenessSettings.d.js";
import type { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "ResponsivenessSettings" as const;

export enum LORA_MODE {
    CLASS_A = "CLASS_A",
    CLASS_B = "CLASS_B",
}

/**
 * These settings control how responsive the device will be. Any changes here will be downlinked to the relevant
 * devices as soon as possible.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@pre<ResponsivenessSettings>("save", async function (this: DocumentType<ResponsivenessSettings>) {
    const validationResult = ResponsivenessSettings.getters.validate(this);
    if (validationResult && validationResult[0] === false) {
        throw new Error(`Validation error in ResponsivenessSettings: ${validationResult[1]}`);
    }
})
@post<SchemaBase<ResponsivenessSettings>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ResponsivenessSettings extends SchemaBase<
    ResponsivenessSettings,
    ResponsivenessSettingsFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * The period of time in ms after which a device should send another uplink. In class A use, this has a direct
     * relationship to response time. In class B, we need to keep it short enough for us to be confident the
     * device has not gone offline
     */
    @validate({ zodValidator: milliseconds })
    @prop({ type: () => Number, required: true })
    public uplinkPeriod!: number;

    /**
     * Class A means the device dictates the communications and we need to wait for an uplink slot to send a message
     * Class B means we can send downlinks whenever we like for a much faster response time.
     */
    @validate({ zodValidator: z.enum(LORA_MODE) })
    @prop({ type: () => String, required: true })
    public loraMode!: LORA_MODE;

    /**
     * This number to the power of 2 is the number of seconds between downlink receive windows on a
     * class B device
     *
     * - Not in Ms!
     * - Represents the time between pings for class B devices.
     * - Must be an integer between 0 and 7 (inclusive).
     *
     * If doing response time math, use the relevant utility function instead of using this directly.
     */
    @validate({ zodValidator: z.number().int().min(0).max(7).optional() })
    @prop({ type: () => Number, required: false })
    public pingSlotPeriodicity?: number;

    /**
     * If packet loss exceeds this threshold, packet loss will be marked as red and the device
     * will get a warning. This will be used inform worst-case response time, which may appear in areas
     * like the manual override mode.
     */
    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public maximumPermissiblePacketLoss!: number;

    /**
     * If this much time elapses with no communication with the device, it will be considered offline and a
     * warning will be generated
     */
    @validate({ zodValidator: milliseconds })
    @prop({ type: () => Number, required: true })
    public timeUntilConsideredOffline!: number;

    static getters = {
        /**
         * Validates ResponsivenessSettings configuration.
         *
         * @param self - ResponsivenessSettings instance
         * @returns [true] if valid, or [false, errorMessage] if invalid
         */
        validate: (self): [false, string] | [true] => {
            // Validate loraMode is CLASS_A or CLASS_B
            if (![LORA_MODE.CLASS_A, LORA_MODE.CLASS_B].includes(self.loraMode)) {
                return [false, `lora mode must be ${LORA_MODE.CLASS_A} or ${LORA_MODE.CLASS_B}`];
            }

            // CLASS_B requires pingSlotPeriodicity
            if (self.loraMode === LORA_MODE.CLASS_B && self.pingSlotPeriodicity === undefined) {
                return [false, "Class B devices require a ping period"];
            }

            // CLASS_A must not have pingSlotPeriodicity
            if (self.loraMode !== LORA_MODE.CLASS_B && self.pingSlotPeriodicity !== undefined) {
                return [false, "Only class B devices can have a ping period"];
            }

            // Validate pingSlotPeriodicity range (0-7)
            if (self.pingSlotPeriodicity !== undefined) {
                if (self.pingSlotPeriodicity < 0 || self.pingSlotPeriodicity > 7) {
                    return [false, "ping slot periodicity must be an integer between 0 and 7 (inclusive)"];
                }
            }

            // Validate minimum uplink period (30 seconds)
            if (self.uplinkPeriod < 30 * secondMs) {
                return [false, "uplink period may not be less than 30 seconds"];
            }

            return [true];
        },

        /**
         * Gets the response time of the device in the best case scenario. This is equivalent to
         * uplink period or periodicity depending on lora device class.
         * @param self
         */
        getBestCaseResponseTime(self): number {
            if (self.loraMode === LORA_MODE.CLASS_A) {
                return self.uplinkPeriod;
            } else if (self.loraMode === LORA_MODE.CLASS_B) {
                return Math.pow(2, self.pingSlotPeriodicity) * secondMs;
            }
            throw new Error(`Invalid Lora mode ${self.loraMode} when calculating response time!`);
        },

        /**
         * Gets the response time of the device in the worst case scenario. This assumes an end-to-end packet
         * loss of self.maximumPermissiblePacketLoss and the device should respond within this time, confidence
         * interval of 95%.
         *
         * This does not round the answer to the nearest uplink/receive window period because it assumes a randomly distributed
         * delay until the first receive window opens up.
         * @param self
         */
        getWorstCaseResponseTime(self): number {
            if (self.maximumPermissiblePacketLoss <= 0 || self.maximumPermissiblePacketLoss >= 1) {
                return NaN;
            }

            const confidenceInterval = 0.95;
            const receiveWindowsRequired = Math.max(
                logBase(1 - confidenceInterval, self.maximumPermissiblePacketLoss),
                1.0,
            );

            return ResponsivenessSettings.getters.getBestCaseResponseTime(self) * receiveWindowsRequired;
        },
    } as const satisfies Record<string, (self: ResponsivenessSettings) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ResponsivenessSettingsSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ResponsivenessSettings, ResponsivenessSettingsFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ResponsivenessSettings.dtoValidators = getZodSchemaFromClass(
    ResponsivenessSettings,
) as unknown as DtoValidatorTypes<ResponsivenessSettingsSchemaTypes>;
